import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { SendIcon, XCircleIcon, RefreshCwIcon, EyeOffIcon, XIcon, Edit3Icon, GripHorizontalIcon, SaveIcon, PackageIcon } from 'lucide-react';
import { DocumentSidebar } from './DocumentSidebar';
import { DocumentDataPanel } from './DocumentDataPanel';
import { ImageViewerPanel } from './ImageViewerPanel';
import { LineItemsPanel } from './LineItemsPanel';
import { ValidationPanel } from './ValidationPanel';
import { DraftChangesPopover } from './DraftChangesPopover';
import { Button } from './ui/Button';
import { mockInvoices, getInvoiceDetails, mockInvoice1Document, mockInvoice2Document, mockInvoice3Document, mockInvoiceDocument, mockInvoice5Document, mockCreditNoteDocument, mock2WayMatchingDocument, mock3WayMatchingDocument } from '../data/mockInvoices';
import { LineItem, InvoiceDocument } from '../types/invoice';
import { useDraftState } from '../hooks/useDraftState';
import { validateInvoice } from '../utils/invoiceValidation';
import { useKeyboardShortcuts, KeyboardShortcut } from '../hooks/useKeyboardShortcuts';
import { KeyboardShortcutsHelp } from './KeyboardShortcutsHelp';
import { ReassignModal } from './ReassignModal';
import { POMatchingModal } from './POMatchingModal';
const DETACHED_VIEWER_CHANNEL = 'document-viewer-sync';
export function DocumentManagementScreen() {
  const [selectedDocumentId, setSelectedDocumentId] = useState('4');
  const [isEditAllMode, setIsEditAllMode] = useState(false);
  const [isViewerDetached, setIsViewerDetached] = useState(false);
  const [showShortcutsHelp, setShowShortcutsHelp] = useState(false);
  const [validationExpanded, setValidationExpanded] = useState(false);
  const [validationHovered, setValidationHovered] = useState(false);
  const [showReassignModal, setShowReassignModal] = useState(false);
  const [showPOMatchingModal, setShowPOMatchingModal] = useState(false);
  // Make invoices stateful so we can update assignments
  const [invoices, setInvoices] = useState(mockInvoices);
  // Resizable panel state - 50/50 split by default to give more room for expanded line items
  const [topPanelPercentage, setTopPanelPercentage] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const dragStartY = useRef(0);
  const dragStartPercentage = useRef(0);
  const broadcastChannelRef = useRef<BroadcastChannel | null>(null);
  const detachedWindowRef = useRef<Window | null>(null);
  // Determine which document to use based on selected document
  const selectedDocument = invoices.find(inv => inv.id === selectedDocumentId);
  const documentType = selectedDocument?.documentType === 'Credit Note' ? 'Credit Note' : 'Invoice';
  // Select the appropriate base document based on document ID and type
  const getBaseDocument = () => {
    switch (selectedDocumentId) {
      case '1':
        return mockInvoice1Document;
      case '2':
        return mockInvoice2Document;
      case '3':
        return mockInvoice3Document;
      case '4':
        return mockInvoiceDocument;
      case '5':
        return mockInvoice5Document;
      case '6':
        return mockCreditNoteDocument;
      case '7':
        return mock2WayMatchingDocument;
      case '8':
        return mock3WayMatchingDocument;
      default:
        return mockInvoiceDocument;
    }
  };
  const baseDocument = getBaseDocument();
  const {
    document: documentData,
    originalDocument,
    hasDraft,
    saveDraft,
    clearDraft,
    submitDraft
  } = useDraftState(selectedDocumentId, baseDocument);
  // Generate document details from current document
  const documentDetails = useMemo(() => getInvoiceDetails(documentData), [documentData]);
  // Validate document whenever it changes
  const validation = useMemo(() => {
    return validateInvoice(documentData, documentType);
  }, [documentData, documentType]);
  // Initialize broadcast channel and sync with detached viewer
  useEffect(() => {
    // Check if viewer is already open
    const viewerOpen = localStorage.getItem('detached_viewer_open') === 'true';
    setIsViewerDetached(viewerOpen);
  }, []);
  // Sync document changes to detached viewer via postMessage
  useEffect(() => {
    // Send message to detached viewer window if it exists
    if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
      detachedWindowRef.current.postMessage({
        type: 'DOCUMENT_CHANGED',
        documentId: selectedDocumentId
      }, '*');
    }
    // Also update localStorage as fallback
    localStorage.setItem('current_document_id', selectedDocumentId);
  }, [selectedDocumentId]);
  // Listen for messages from detached viewer
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'VIEWER_READY') {
        // Send current document to newly opened viewer
        if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
          detachedWindowRef.current.postMessage({
            type: 'DOCUMENT_CHANGED',
            documentId: selectedDocumentId
          }, '*');
        }
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [selectedDocumentId]);
  // Handle detach viewer button click
  const handleDetachViewer = () => {
    if (isViewerDetached && detachedWindowRef.current && !detachedWindowRef.current.closed) {
      // Focus existing detached viewer window
      detachedWindowRef.current.focus();
    } else {
      // Set initial document in localStorage before opening window
      localStorage.setItem('current_document_id', selectedDocumentId);
      localStorage.setItem('detached_viewer_open', 'true');
      // Open new detached viewer window
      const viewerUrl = `${window.location.origin}${window.location.pathname}?viewer=detached`;
      const viewerWindow = window.open(viewerUrl, 'DocumentViewer', 'width=1200,height=800,menubar=no,toolbar=no,location=no,status=no');
      if (viewerWindow) {
        detachedWindowRef.current = viewerWindow;
        setIsViewerDetached(true);
        // Adjust panel split to 50/50 when detached
        setTopPanelPercentage(50);
        // Check if window was closed
        const checkClosed = setInterval(() => {
          if (viewerWindow.closed) {
            clearInterval(checkClosed);
            setIsViewerDetached(false);
            localStorage.removeItem('detached_viewer_open');
            detachedWindowRef.current = null;
            // Restore original 50/50 split
            setTopPanelPercentage(50);
          }
        }, 1000);
      }
    }
  };
  // Handle attach viewer button click
  const handleAttachViewer = () => {
    // Close the detached viewer window
    if (detachedWindowRef.current && !detachedWindowRef.current.closed) {
      detachedWindowRef.current.close();
    }
    // Update state and localStorage
    setIsViewerDetached(false);
    localStorage.removeItem('detached_viewer_open');
    detachedWindowRef.current = null;
    // Restore original 50/50 split
    setTopPanelPercentage(50);
  };
  // Handle resize drag start
  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
    dragStartY.current = e.clientY;
    dragStartPercentage.current = topPanelPercentage;
  };
  // Handle resize drag
  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isDragging || !containerRef.current) return;
      const containerHeight = containerRef.current.clientHeight;
      const deltaY = e.clientY - dragStartY.current;
      const deltaPercentage = deltaY / containerHeight * 100;
      const newPercentage = dragStartPercentage.current + deltaPercentage;
      // Clamp between 20% and 80%
      const clampedPercentage = Math.max(20, Math.min(80, newPercentage));
      setTopPanelPercentage(clampedPercentage);
    };
    const handleMouseUp = () => {
      setIsDragging(false);
    };
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'ns-resize';
      document.body.style.userSelect = 'none';
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isDragging]);
  const handleUpdateLineItem = useCallback((index: number, updatedItem: LineItem) => {
    saveDraft((currentDoc: InvoiceDocument) => {
      let updatedLineItems: LineItem[];
      // Check if we're adding a new line (index equals array length)
      if (index === currentDoc.lineItems.length) {
        // Adding new line at the end
        updatedLineItems = [...currentDoc.lineItems, updatedItem];
      } else {
        // Updating existing line
        updatedLineItems = currentDoc.lineItems.map((item, i) => i === index ? updatedItem : item);
      }
      const newSubtotal = updatedLineItems.reduce((sum, item) => sum + item.subtotal, 0);
      const newTaxAmt = updatedLineItems.reduce((sum, item) => sum + (typeof item.taxAmt === 'number' ? item.taxAmt : 0), 0);
      const newTotal = updatedLineItems.reduce((sum, item) => sum + item.total, 0);
      return {
        ...currentDoc,
        lineItems: updatedLineItems,
        subtotal: Math.round(newSubtotal * 100) / 100,
        taxAmt: Math.round(newTaxAmt * 100) / 100,
        total: Math.round(newTotal * 100) / 100
      };
    });
  }, [saveDraft]);
  const handleUpdateDocument = useCallback((updates: Partial<InvoiceDocument>) => {
    saveDraft((currentDoc: InvoiceDocument) => ({
      ...currentDoc,
      ...updates
    }));
  }, [saveDraft]);
  const handleSubmit = useCallback(() => {
    if (!validation.isValid) {
      alert('Cannot submit: Please fix validation errors first.');
      return;
    }
    const submittedDocument = submitDraft();
    // In real app, send submittedDocument to backend
    console.log('Submitting document:', submittedDocument);
    alert('Document submitted successfully!');
  }, [validation.isValid, submitDraft]);
  const handleSaveDraft = useCallback(() => {
    // Draft is already saved via saveDraft calls, no need for alert
    // Just silently confirm the save
  }, []);
  const handleClearDraft = () => {
    clearDraft();
  };
  // Navigation handlers for keyboard shortcuts
  const handleNextDocument = useCallback(() => {
    const currentIndex = mockInvoices.findIndex(inv => inv.id === selectedDocumentId);
    if (currentIndex < mockInvoices.length - 1) {
      setSelectedDocumentId(mockInvoices[currentIndex + 1].id);
    }
  }, [selectedDocumentId]);
  const handlePreviousDocument = useCallback(() => {
    const currentIndex = mockInvoices.findIndex(inv => inv.id === selectedDocumentId);
    if (currentIndex > 0) {
      setSelectedDocumentId(mockInvoices[currentIndex - 1].id);
    }
  }, [selectedDocumentId]);
  const handleReject = useCallback(() => {
    if (confirm('Are you sure you want to reject this document?')) {
      console.log('Rejecting document:', selectedDocumentId);
      alert('Document rejected!');
      // In real app, send rejection to backend
    }
  }, [selectedDocumentId]);
  const handleToggleEditAll = useCallback(() => {
    setIsEditAllMode(prev => !prev);
  }, []);
  const handleCancel = useCallback(() => {
    if (isEditAllMode) {
      setIsEditAllMode(false);
    } else if (hasDraft) {
      handleClearDraft();
    }
  }, [isEditAllMode, hasDraft]);
  const handleReassign = useCallback((assigneeId: string, assigneeType: 'user' | 'team') => {
    // Map user IDs to display names
    const userNames: Record<string, string> = {
      'user-1': 'Sarah Chen',
      'user-2': 'Mike Johnson',
      'user-3': 'Emma Wilson',
      'user-4': 'James Brown',
      'user-5': 'Lisa Martinez',
      'user-6': 'David Lee'
    };
    // Map team IDs to display names
    const teamNames: Record<string, string> = {
      'team-1': 'AP Team',
      'team-2': 'Finance Team',
      'team-3': 'Procurement Team'
    };
    // Get the display name for the assignee
    const displayName = assigneeType === 'user' ? userNames[assigneeId] || assigneeId : teamNames[assigneeId] || assigneeId;
    // Update the invoice assignment
    setInvoices(prevInvoices => prevInvoices.map(inv => inv.id === selectedDocumentId ? {
      ...inv,
      team: displayName,
      lockedBy: assigneeType === 'user' ? displayName : inv.lockedBy
    } : inv));
    // Navigate to next document
    const currentIndex = invoices.findIndex(inv => inv.id === selectedDocumentId);
    if (currentIndex < invoices.length - 1) {
      setSelectedDocumentId(invoices[currentIndex + 1].id);
    } else if (currentIndex > 0) {
      // If at the end, go to first document
      setSelectedDocumentId(invoices[0].id);
    }
  }, [selectedDocumentId, invoices]);
  // Define keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = useMemo(() => [{
    key: 'ArrowRight',
    ctrlKey: true,
    handler: handleNextDocument,
    description: 'Next document'
  }, {
    key: 'd',
    ctrlKey: true,
    handler: handleNextDocument,
    description: 'Next document (alternative)'
  }, {
    key: 'ArrowLeft',
    ctrlKey: true,
    handler: handlePreviousDocument,
    description: 'Previous document'
  }, {
    key: 'd',
    ctrlKey: true,
    shiftKey: true,
    handler: handlePreviousDocument,
    description: 'Previous document (alternative)'
  }, {
    key: 'Enter',
    ctrlKey: true,
    handler: handleSubmit,
    description: 'Submit/Approve document'
  }, {
    key: 's',
    ctrlKey: true,
    handler: handleSaveDraft,
    description: 'Save draft'
  }, {
    key: 'r',
    ctrlKey: true,
    handler: handleReject,
    description: 'Reject document'
  }, {
    key: 'e',
    ctrlKey: true,
    handler: handleToggleEditAll,
    description: 'Toggle Edit All mode'
  }, {
    key: 'Escape',
    handler: handleCancel,
    description: 'Cancel/Exit edit mode',
    preventDefault: false
  }, {
    key: '?',
    shiftKey: true,
    handler: () => setShowShortcutsHelp(prev => !prev),
    description: 'Show keyboard shortcuts'
  }], [handleNextDocument, handlePreviousDocument, handleSubmit, handleSaveDraft, handleReject, handleToggleEditAll, handleCancel]);
  // Register keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts,
    enabled: true
  });
  const hasValidationIssues = validation.errors.length > 0 || validation.warnings.length > 0;
  return <div className="flex h-screen bg-gray-50" data-id="element-559">
      {/* Left Sidebar */}
      <DocumentSidebar documents={invoices} selectedDocumentId={selectedDocumentId} onSelectDocument={setSelectedDocumentId} currentDocument={documentData} data-id="element-560" />

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden" data-id="element-561">
        {/* Top Action Bar */}
        <div className="bg-white border-b border-gray-200 px-6 py-3 flex items-center justify-between gap-2 flex-shrink-0" data-id="element-562">
          {/* Left Side - Draft Status and Validation */}
          <div className="flex items-center gap-3" data-id="element-563">
            {hasDraft && <DraftChangesPopover originalDocument={originalDocument} draftDocument={documentData} onClearDraft={handleClearDraft} data-id="element-564" />}

            {/* Validation Status Indicator */}
            {validation.isValid ? <div className="flex items-center gap-2 px-3 py-1.5 bg-green-50 border border-green-200 rounded-lg" data-id="element-565">
                <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-566">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-567" />
                </svg>
                <span className="text-sm font-medium text-green-700" data-id="element-568">
                  Validation Passed
                </span>
              </div> : hasValidationIssues && <div className="relative" onMouseEnter={() => setValidationHovered(true)} onMouseLeave={() => setValidationHovered(false)} data-id="element-569">
                  <button onClick={() => {
              setValidationExpanded(!validationExpanded);
              // Clear hover state when clicking to ensure dropdown closes immediately
              if (validationExpanded) {
                setValidationHovered(false);
              }
            }} className="flex items-center gap-2 px-3 py-1.5 bg-red-50 border border-red-200 rounded-lg hover:bg-red-100 transition-colors" data-id="element-570">
                    <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-571">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-572" />
                    </svg>
                    <span className="text-sm font-medium text-red-700" data-id="element-573">
                      {validation.errors.length} Error
                      {validation.errors.length !== 1 ? 's' : ''}
                      {validation.warnings.length > 0 && `, ${validation.warnings.length} Warning${validation.warnings.length !== 1 ? 's' : ''}`}
                    </span>
                    <svg className={`w-4 h-4 text-red-600 transition-transform ${validationExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-574">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" data-id="element-575" />
                    </svg>
                  </button>

                  {/* Dropdown Panel */}
                  {(validationExpanded || !validationExpanded && validationHovered) && <div className="absolute top-full left-0 mt-2 w-96 bg-white border border-gray-200 rounded-lg shadow-xl z-50 max-h-96 overflow-y-auto" data-id="element-576">
                      <div className="p-4 space-y-4" data-id="element-577">
                        {/* Errors */}
                        {validation.errors.length > 0 && <div data-id="element-578">
                            <h4 className="text-sm font-semibold text-red-900 mb-2 flex items-center gap-2" data-id="element-579">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-580">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" data-id="element-581" />
                              </svg>
                              Errors ({validation.errors.length})
                            </h4>
                            <div className="space-y-2" data-id="element-582">
                              {validation.errors.map((error, index) => <div key={index} className="bg-red-50 border border-red-200 rounded p-2" data-id="element-583">
                                  <div className="text-xs font-semibold text-red-900 mb-1" data-id="element-584">
                                    {error.rule}
                                  </div>
                                  <div className="text-xs text-red-800" data-id="element-585">
                                    {error.message}
                                  </div>
                                  {error.field && <div className="text-xs text-red-600 mt-1" data-id="element-586">
                                      Field: {error.field}
                                    </div>}
                                </div>)}
                            </div>
                          </div>}

                        {/* Warnings */}
                        {validation.warnings.length > 0 && <div data-id="element-587">
                            <h4 className="text-sm font-semibold text-amber-900 mb-2 flex items-center gap-2" data-id="element-588">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-589">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" data-id="element-590" />
                              </svg>
                              Warnings ({validation.warnings.length})
                            </h4>
                            <div className="space-y-2" data-id="element-591">
                              {validation.warnings.map((warning, index) => <div key={index} className="bg-amber-50 border border-amber-200 rounded p-2" data-id="element-592">
                                  <div className="text-xs font-semibold text-amber-900 mb-1" data-id="element-593">
                                    {warning.rule}
                                  </div>
                                  <div className="text-xs text-amber-800" data-id="element-594">
                                    {warning.message}
                                  </div>
                                  {warning.field && <div className="text-xs text-amber-600 mt-1" data-id="element-595">
                                      Field: {warning.field}
                                    </div>}
                                </div>)}
                            </div>
                          </div>}

                        {/* Footer */}
                        <div className="pt-2 border-t border-gray-200" data-id="element-596">
                          <p className="text-xs text-gray-600" data-id="element-597">
                            Click to{' '}
                            {validationExpanded ? 'close' : 'keep open'} • Hover
                            to preview
                          </p>
                        </div>
                      </div>
                    </div>}
                </div>}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-3" data-id="element-598">
            {/* Keyboard Shortcuts Help Button */}
            <Button variant="ghost" size="sm" onClick={() => setShowShortcutsHelp(true)} className="text-gray-500 hover:text-gray-700" title="Keyboard shortcuts (Shift+?)" data-id="element-599">
              <span className="text-sm font-medium" data-id="element-600">?</span>
            </Button>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300" data-id="element-601"></div>

            {/* Primary Actions - Submit & Reject */}
            <Button variant="primary" size="md" onClick={handleSubmit} disabled={!validation.isValid} className="shadow-sm" title="Submit document (Ctrl+Enter)" data-id="element-602">
              <SendIcon className="w-4 h-4 mr-2" data-id="element-603" />
              Submit
            </Button>
            <Button variant="danger" size="md" className="shadow-sm" onClick={handleReject} title="Reject document (Ctrl+R)" data-id="element-604">
              <XCircleIcon className="w-4 h-4 mr-2" data-id="element-605" />
              Reject
            </Button>
            <Button variant="secondary" size="md" onClick={handleSaveDraft} disabled={!hasDraft} className="shadow-sm" title="Save draft (Ctrl+S)" data-id="element-606">
              <SaveIcon className="w-4 h-4 mr-2" data-id="element-607" />
              Save Draft
            </Button>

            {/* Divider */}
            <div className="w-px h-8 bg-gray-300" data-id="element-608"></div>

            {/* Secondary Actions */}
            <Button variant="ghost" size="sm" onClick={() => setShowReassignModal(true)} data-id="element-609">
              <RefreshCwIcon className="w-4 h-4 mr-2" data-id="element-610" />
              Reassign
            </Button>

            {isViewerDetached ? <Button variant="ghost" size="sm" onClick={handleAttachViewer} data-id="element-611">
                <EyeOffIcon className="w-4 h-4 mr-2" data-id="element-612" />
                Attach Viewer
              </Button> : <Button variant="ghost" size="sm" onClick={handleDetachViewer} data-id="element-613">
                <EyeOffIcon className="w-4 h-4 mr-2" data-id="element-614" />
                Detach Viewer
              </Button>}

            {/* Edit All Button */}
            <Button variant="ghost" size="sm" onClick={handleToggleEditAll} title="Toggle Edit All mode (Ctrl+E)" data-id="element-615">
              <Edit3Icon className="w-4 h-4 mr-2" data-id="element-616" />
              {isEditAllMode ? 'Save All' : 'Edit All'}
            </Button>

            <Button variant="ghost" size="sm" onClick={handleCancel} title="Cancel (Escape)" data-id="element-617">
              <XIcon className="w-4 h-4 mr-2" data-id="element-618" />
              Cancel
            </Button>
          </div>
        </div>

        {/* Resizable Content Area */}
        <div ref={containerRef} className="flex-1 flex flex-col overflow-hidden" data-id="element-619">
          {/* Top Panel - Document Data and Image Viewer (Resizable) */}
          <div className="px-4 pt-4 flex-shrink-0 overflow-hidden" style={{
          height: `${topPanelPercentage}%`
        }} data-id="element-620">
            <div className={`${isViewerDetached ? 'grid-cols-1' : 'grid grid-cols-2'} gap-4 h-full`} data-id="element-621">
              <div className="h-full overflow-hidden" data-id="element-622">
                <DocumentDataPanel details={documentDetails} document={documentData} isEditAllMode={isEditAllMode} onUpdateDocument={handleUpdateDocument} isViewerDetached={isViewerDetached} validation={validation} data-id="element-623" />
              </div>
              {!isViewerDetached && <div className="h-full overflow-hidden" data-id="element-624">
                  <ImageViewerPanel document={documentData} data-id="element-625" />
                </div>}
            </div>
          </div>

          {/* Resize Handle */}
          <div className="flex-shrink-0 px-4 py-2 cursor-ns-resize group" onMouseDown={handleMouseDown} data-id="element-626">
            <div className={`h-1 rounded-full transition-colors ${isDragging ? 'bg-blue-500' : 'bg-gray-300 group-hover:bg-blue-400'}`} data-id="element-627">
              <div className="flex items-center justify-center -mt-2" data-id="element-628">
                <div className={`px-3 py-1 rounded-full transition-all ${isDragging ? 'bg-blue-500 shadow-lg' : 'bg-gray-300 group-hover:bg-blue-400 opacity-0 group-hover:opacity-100'}`} data-id="element-629">
                  <GripHorizontalIcon className="w-4 h-4 text-white" data-id="element-630" />
                </div>
              </div>
            </div>
          </div>

          {/* Bottom Panel - Line Items (Flexible) */}
          <div className="flex-1 px-4 pb-4 min-h-0 overflow-hidden" data-id="element-631">
            <div className="h-full overflow-y-auto overflow-x-hidden" data-id="element-632">
              <LineItemsPanel document={documentData} onUpdateLineItem={handleUpdateLineItem} validation={validation} isEditAllMode={isEditAllMode} data-id="element-633" />
            </div>
          </div>
        </div>
      </div>

      {showShortcutsHelp && <KeyboardShortcutsHelp shortcuts={shortcuts} onClose={() => setShowShortcutsHelp(false)} data-id="element-634" />}

      {showReassignModal && <ReassignModal isOpen={showReassignModal} onClose={() => setShowReassignModal(false)} documentAmount={documentData.total} currentAssignee="Current User" onReassign={handleReassign} data-id="element-635" />}

      {showPOMatchingModal && <POMatchingModal vendorName={documentData.supplier} invoiceTotal={documentData.total} invoiceCurrency={documentData.currency} onApply={(poNumber, selectedLines, matchingType) => {
      console.log('PO Matching applied:', {
        poNumber,
        selectedLines,
        matchingType
      });
      // TODO: Apply the PO matching to the document
      setShowPOMatchingModal(false);
    }} onClose={() => setShowPOMatchingModal(false)} data-id="element-636" />}
    </div>;
}